// apps/web/src/context/StartedTrainingContext.tsx
import * as React from 'react';

export type ExerciseDTO = { id: string; nameRaw?: string | null; raw?: string | null; targetSets?: number | null; repsPerSet?: number | null; };

export type StartedTrainingSetRecord = {
  weight: number | null;
  timeMs: number | null;
  // Pause accrued while this set was the current one. Optional, not `| null` with
  // a default: every snapshot already sitting in localStorage was written before
  // this field existed and still has to resume, and the dozen places that seed an
  // empty row as `{ weight: null, timeMs: null }` stay valid.
  pauseMs?: number | null;
};

export type StartedTrainingExerciseProgress = {
  sets: StartedTrainingSetRecord[];
};

export type StartedTrainingResumeState = {
  blockIdx: number;
  stepIdx: number;
  pendingNextMethodIdx: number | null;
  workoutMs: number;
  workoutStartedAtMs: number | null;
  exerciseMs: number;
  paused: boolean;
  progress: StartedTrainingExerciseProgress[];
  repOverrides: Array<Array<number | null>>;
  amrapRounds: number;
  amrapResults: Array<{ blockId: string; durationMin: number; rounds: number }>;
  amrapRoundTimes?: Array<{ blockId: string; roundTimesMs: number[] }>;
  methodStartOffsetMs: number;
  // Two different clocks: `pauseElapsedMs` is the *current set's* pause (the
  // footer's right-hand number, reset at every set boundary), `pauseTotalMs` is
  // every pause since the workout started. Optional so an older snapshot resumes;
  // there it seeds from pauseElapsedMs, which was the closest thing to a total.
  pauseElapsedMs: number;
  pauseTotalMs?: number;
  weightInput: number | null;
};

export type PlannedTrainingDTO = {
  id: string;
  plannedId?: string | null;
  globalStartedAt?: string | null;
  globalStartedBy?: string | null;
  coachFriendId?: string | null;
  requiresGlobalStart?: boolean | null;
  readyByMe?: boolean | null;
  notes?: string | null;
  tz?: string | null;
  start?: string | null;         // UTC ISO from API
  plannerMeta?: any | null;
  exercises?: ExerciseDTO[];
  method?: { id: string; name?: string | null; type?: string | null } | null;
  title?: string | null;
  resumeMode?: 'resume' | 'fresh';
  resumeState?: StartedTrainingResumeState | null;
};

export type PartnerSessionCtx = {
  sessionId: string;
  role: 'host' | 'guest';
  selfUserId: string;
  selfName: string;
  partnerName?: string | null;
};

type Ctx = {
  started: PlannedTrainingDTO | null;
  partner: PartnerSessionCtx | null;
  startTraining: (t: PlannedTrainingDTO, partner?: PartnerSessionCtx | null) => void;
  stopTraining: () => void;
};

const StartedCtx = React.createContext<Ctx | undefined>(undefined);

export function StartedTrainingProvider({ children }: { children: React.ReactNode }) {
  const [started, setStarted] = React.useState<PlannedTrainingDTO | null>(null);
  const [partner, setPartner] = React.useState<PartnerSessionCtx | null>(null);
const startTraining = React.useCallback((t: PlannedTrainingDTO, p?: PartnerSessionCtx | null) => {
  setStarted({
    ...t,
    plannedId: t.plannedId ?? t.id ?? null,
  });
  setPartner(p ?? null);
}, []);
  const stopTraining  = React.useCallback(() => { setStarted(null); setPartner(null); }, []);
  return (
    <StartedCtx.Provider value={{ started, partner, startTraining, stopTraining }}>
      {children}
    </StartedCtx.Provider>
  );
}

export function useStartedTraining() {
  const ctx = React.useContext(StartedCtx);
  if (!ctx) throw new Error('useStartedTraining must be used within StartedTrainingProvider');
  return ctx;
}
